import type { Table } from 'apache-arrow'
import type { BlastSource } from '../../Model/Project/BlastSource.js'
import type { SpinSelection } from './CubeKey.js'
import { cubeKey } from './CubeKey.js'
import { recordCubeDiag } from './CubeDiagnostics.js'

/**
 * Process-local registry of decoded Arrow `Table`s, keyed by `(scopeId, blastType[, spin])`.
 *
 * Why this sits outside Redux state: Arrow `Table`s hold native (typed-array / WASM-style)
 * buffers and aren't true plain-JS values. Putting them in Redux would either trip the
 * default `serializableCheck` middleware or require permanent exclusion paths. We instead
 * keep the slice carrying only metadata (status / etag / loadedAt) and look the table up
 * here at selector time.
 *
 * Lifecycle:
 * - The cube loader epic decodes IPC bytes and calls [registerCubeTable] before dispatching
 *   `cubeReady` — so by the time any selector runs, the table is here.
 * - The registry is process-local. Across page reloads, the slice + IndexedDB hand off; the
 *   epic re-decodes from cached bytes and re-registers.
 *
 * Eviction: count-based LRU bounded to [MAX_RESIDENT_TABLES] entries. We do NOT cap by byte
 * size on purpose — a single project can legitimately produce a 500MB cube and that needs
 * to render. Capping by count guards against the wild-clicking-across-100-projects case
 * without artificially refusing big-but-real cubes. Eviction is a no-op for live render trees
 * (closures still hold the table); the eviction just prevents the registry itself from
 * being a permanent leak channel.
 *
 */

export const MAX_RESIDENT_TABLES = 3

interface ResidentTable {
  table: Table
  /** Decompressed IPC byte size the table was decoded from; 0 if unknown. For diagnostics only. */
  bytes: number
}

/** A resident table plus its key, most-recent-first. For the diagnostics widget. */
export interface ResidentTableSummary {
  key: string
  rows: number
  bytes: number
}

// `Map` preserves insertion order, so the LRU is implemented by deleting + re-inserting on
// touch. Insertion order then doubles as recency, with the oldest entry being `keys().next()`.
const tables = new Map<string, ResidentTable>()

const tableKey = (scopeId: string, blastType: BlastSource, spin?: SpinSelection): string =>
  cubeKey(scopeId, blastType, spin)

const touch = (key: string, entry: ResidentTable): void => {
  // Delete-then-set moves the key to the most-recent position regardless of whether it existed.
  tables.delete(key)
  tables.set(key, entry)
}

const evictIfNeeded = (): void => {
  while (tables.size > MAX_RESIDENT_TABLES) {
    const oldest = tables.keys().next().value
    if (oldest === undefined) return
    tables.delete(oldest)
    recordCubeDiag({ kind: 'registry-evict', key: oldest, detail: `over MAX_RESIDENT_TABLES=${MAX_RESIDENT_TABLES}` })
  }
}

/**
 * Register a decoded table. [bytes] is the decompressed IPC size it was decoded from (the loader
 * epic has it for free); it's stored for diagnostics only and defaults to 0 when unknown.
 */
export const registerCubeTable = (
  scopeId: string,
  blastType: BlastSource,
  table: Table,
  bytes = 0,
  spin?: SpinSelection
): void => {
  touch(tableKey(scopeId, blastType, spin), { table, bytes })
  evictIfNeeded()
}

export const getCubeTable = (scopeId: string, blastType: BlastSource, spin?: SpinSelection): Table | undefined => {
  const key = tableKey(scopeId, blastType, spin)
  const entry = tables.get(key)
  if (entry) touch(key, entry)
  return entry?.table
}

export const clearCubeTable = (scopeId: string, blastType: BlastSource, spin?: SpinSelection): void => {
  tables.delete(tableKey(scopeId, blastType, spin))
}

export const clearAllCubeTables = (): void => {
  tables.clear()
}

/** For tests/diagnostics. Returns resident table keys in LRU order (most recent first). */
export const debugListResidentTables = (): string[] => [...tables.keys()].reverse()

/** Resident tables with row counts + byte sizes, most-recent-first. Drives the diagnostics widget. */
export const getResidentTableSummaries = (): ResidentTableSummary[] =>
  [...tables.entries()].reverse().map(([key, entry]) => ({
    key,
    rows: entry.table.numRows,
    bytes: entry.bytes
  }))
